
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

// Hard costs associated with this campaign
export type CampaignHardCosts = {id: string, incurredOn: string, item: string, cost: number};

// Period by period costs for this campaign
export type CampaignRecurringCosts = {startDate: string, endDate: string, cost: number};

 /**
 * Campaign
 *
 * A marketing campaign that a law firm runs to generate leads, spanning channels such as ads, agencies, social media, sponsorships, and referral networks. Each campaign tracks its full cost — one-off hard costs, recurring period costs, and attorney labor time — so the firm can calculate return on investment by tracing leads through to billed and collected revenue.
 */
class Campaign extends Base implements EntityType {
    campaignId: uuid;
    name: string;
    category: string;
    usesDate: boolean;
    leadDateLabel?: string | null;
    usesCost: boolean;
    cost: number;
    usesPeriod: boolean;
    periodLength: number;
    periodUnit?: string | null;
    usesPeriodFixedCost: boolean;
    periodFixedCost?: number | null;
    hasAdditionalInfo: boolean;
    additionalInfoLabel?: string | null;
    startDate: Date;
    endDate?: Date | null;
    website?: string | null;
    hardCosts: CampaignHardCosts[];
    recurringCosts: CampaignRecurringCosts[];
    closed: boolean;

    /**
     * Create a new Campaign, with an (optional) JSON object or Campaign class
     *
     * @param {Object|Campaign} doc - Can be either a simple JSON object or a Campaign class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Campaign | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            campaignId: genUUID(),
            name: null,
            category: null,
            startDate: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'campaign';
        // @ts-ignore
        this.campaignId = doc._campaignId || doc.campaignId || doc.campaignid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.category = doc._category || doc.category || null;
        // @ts-ignore
        this.usesDate = util.defaultBoolean([doc.usesDate, doc._usesDate, doc.usesdate, true]);
        // @ts-ignore
        this.leadDateLabel = doc._leadDateLabel || doc.leadDateLabel || doc.leaddatelabel || null;
        // @ts-ignore
        this.usesCost = util.defaultBoolean([doc.usesCost, doc._usesCost, doc.usescost, false]);
        // @ts-ignore
        this.cost = util.coalesce(util.parseFloat(doc._cost), util.parseFloat(doc.cost), 0);
        // @ts-ignore
        this.usesPeriod = util.defaultBoolean([doc.usesPeriod, doc._usesPeriod, doc.usesperiod, false]);
        // @ts-ignore
        this.periodLength = util.coalesce(util.parseInt(doc._periodLength), util.parseInt(doc.periodLength), util.parseInt(doc.periodlength), 1);
        // @ts-ignore
        this.periodUnit = doc._periodUnit || doc.periodUnit || doc.periodunit || null;
        // @ts-ignore
        this.usesPeriodFixedCost = util.defaultBoolean([doc.usesPeriodFixedCost, doc._usesPeriodFixedCost, doc.usesperiodfixedcost, false]);
        // @ts-ignore
        this.periodFixedCost = util.coalesce(util.parseFloat(doc._periodFixedCost), util.parseFloat(doc.periodFixedCost), util.parseFloat(doc.periodfixedcost), null);
        // @ts-ignore
        this.hasAdditionalInfo = util.defaultBoolean([doc.hasAdditionalInfo, doc._hasAdditionalInfo, doc.hasadditionalinfo, false]);
        // @ts-ignore
        this.additionalInfoLabel = doc._additionalInfoLabel || doc.additionalInfoLabel || doc.additionalinfolabel || null;
        // @ts-ignore
        this.startDate = DateUtil.timestampOnly(doc._startDate) || DateUtil.timestampOnly(doc.startDate) || DateUtil.timestampOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.timestampOnly(doc._endDate) || DateUtil.timestampOnly(doc.endDate) || DateUtil.timestampOnly(doc.enddate) || null;
        // @ts-ignore
        this.website = doc._website || doc.website || null;
        // @ts-ignore
        this.hardCosts = doc._hardCosts || doc.hardCosts || doc.hardcosts || [];
        // @ts-ignore
        this.recurringCosts = doc._recurringCosts || doc.recurringCosts || doc.recurringcosts || [];
        // @ts-ignore
        this.closed = util.defaultBoolean([doc.closed, doc._closed, doc.closed, false]);

    }
    /**
     * @returns {uuid} This is a generic pointer to the campaignId across all entity types
     */
    get id(): uuid {
        return this.campaignId;
    }


    /**
     * Add a hardCost to campaign.hardCosts
     *
     * @param {CampaignHardCosts} hardCost - The HardCost to add
     * @returns CampaignHardCosts - The hardCost with any defaulted fields set
     */
    addHardCost(hardCost: CampaignHardCosts) : CampaignHardCosts {
        if (!hardCost) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.hardCosts) this.hardCosts = [];
        this.hardCosts.push(hardCost);

        return hardCost;
    }

    /**
     * Update a hardCost from campaign
     *
     * @param {CampaignHardCosts} hardCost - The HardCost to remove
     */
    updateHardCost(hardCost: CampaignHardCosts) {
        if (!this.hardCosts) this.hardCosts = [];
        
        // Find the matching object by hardCost.hardCostId
        const index = this.hardCosts.findIndex(item => item === hardCost);
        if (index !== -1) {
            this.hardCosts[index] = hardCost;
        }
        else this.addHardCost(hardCost);
    }

    /**
     * Remove a hardCost from campaign
     *
     * @param {CampaignHardCosts} hardCost - The HardCost to remove
     */
    removeHardCost(hardCost: CampaignHardCosts) {
        if (!this.hardCosts) return;
        
        // Find the matching object by hardCost.hardCostId
        let foundItems = this.hardCosts?.filter(item => item === hardCost);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.hardCosts = this.hardCosts.filter(item => !(item === hardCost));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonHardCosts() {
        this.hardCosts = [];
     }
        
    /**
     * Add a recurringCost to campaign.recurringCosts
     *
     * @param {CampaignRecurringCosts} recurringCost - The RecurringCost to add
     * @returns CampaignRecurringCosts - The recurringCost with any defaulted fields set
     */
    addRecurringCost(recurringCost: CampaignRecurringCosts) : CampaignRecurringCosts {
        if (!recurringCost) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.recurringCosts) this.recurringCosts = [];
        this.recurringCosts.push(recurringCost);

        return recurringCost;
    }

    /**
     * Update a recurringCost from campaign
     *
     * @param {CampaignRecurringCosts} recurringCost - The RecurringCost to remove
     */
    updateRecurringCost(recurringCost: CampaignRecurringCosts) {
        if (!this.recurringCosts) this.recurringCosts = [];
        
        // Find the matching object by recurringCost.recurringCostId
        const index = this.recurringCosts.findIndex(item => item === recurringCost);
        if (index !== -1) {
            this.recurringCosts[index] = recurringCost;
        }
        else this.addRecurringCost(recurringCost);
    }

    /**
     * Remove a recurringCost from campaign
     *
     * @param {CampaignRecurringCosts} recurringCost - The RecurringCost to remove
     */
    removeRecurringCost(recurringCost: CampaignRecurringCosts) {
        if (!this.recurringCosts) return;
        
        // Find the matching object by recurringCost.recurringCostId
        let foundItems = this.recurringCosts?.filter(item => item === recurringCost);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.recurringCosts = this.recurringCosts.filter(item => !(item === recurringCost));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonRecurringCosts() {
        this.recurringCosts = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this campaign"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.campaignId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["hardCosts", "recurringCosts"];
    }

    /**
     * Default entries are Outlaw-supplied Campaign entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Campaign[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'campaign',
            table: 'CAMPAIGN',
            comment: 'A marketing campaign that a law firm runs to generate leads, spanning channels such as ads, agencies, social media, sponsorships, and referral networks. Each campaign tracks its full cost — one-off hard costs, recurring period costs, and attorney labor time — so the firm can calculate return on investment by tracing leads through to billed and collected revenue.',
            fields: {
                campaignId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this campaign'
                },
                name: {
                    type: 'string',
                    required: true,
                    comment: 'Display name for this campaign'
                },
                category: {
                    type: 'string',
                    required: true,
                    comment: 'What kind of campaign is this?'
                },
                usesDate: {
                    type: 'boolean',
                    required: false,
                    default: true,
                    comment: 'Does this campaign require a date field?'
                },
                leadDateLabel: {
                    type: 'string',
                    required: false,
                    comment: 'If there is a date associated with this campaign, what label should be used?'
                },
                usesCost: {
                    type: 'boolean',
                    required: false,
                    comment: 'Is there a cost associated with this campaign?'
                },
                cost: {
                    type: 'money',
                    required: false,
                    default: 0.0,
                    comment: 'The fixed cost of this campaign'
                },
                // Periodicity
                usesPeriod: {
                    type: 'boolean',
                    required: false,
                    default: false,
                    comment: 'Is there a period (day, week, month...) associated with this campaign?'
                },
                periodLength: {
                    type: 'int',
                    default: 1,
                    required: false,
                    minValue: 1,
                    comment: 'This campaign repeats every {periodLength} {periodUnit}s'
                },
                periodUnit: {
                    type: 'string',
                    required: false,
                    comment: 'days, weeks, months, years'
                },
                usesPeriodFixedCost: {
                    type: 'boolean',
                    default: false,
                    required: false,
                    comment: 'Is the cost the same every period?'
                },
                periodFixedCost: {
                    type: 'money',
                    required: false,
                    comment: 'If the cost is the same every period, we can auto-populate it'
                },
                hasAdditionalInfo: {
                    type: 'boolean',
                    required: false,
                    comment: 'Is any additional information required?'
                },
                additionalInfoLabel: {
                    type: 'string',
                    required: false,
                    comment: 'If additional information is required, what label should be used?'
                },
                startDate: {
                    type: 'timestamp',
                    required: true,
                    comment: 'When did this campaign kick off?'
                },
                endDate: {
                    type: 'timestamp',
                    required: false,
                    comment: 'When will (did) this campaign end?'
                },
                website: {
                    type: 'string',
                    comment: 'Link to the related website'
                },
                hardCosts: {
                    type: 'json[]',
                    required: false,
                    jsdoc: '{id: string, incurredOn: string, item: string, cost: number}',
                    comment: 'Hard costs associated with this campaign'
                },
                recurringCosts: {
                    type: 'json[]',
                    required: false,
                    jsdoc: '{startDate: string, endDate: string, cost: number}',
                    comment: 'Period by period costs for this campaign'
                },
                closed: {
                    type: 'boolean',
                    comment: 'Is this campaign closed? If so it will not be shown in analytics by default'
                }
            },
            externalRefs: [
                'document', 'comment'
            ],
        });
    }

}
export default Campaign;
